import React, { useState, useRef, useEffect } from 'react';
import { X, Lock } from 'lucide-react';

const PinVerificationModal = ({ 
  isOpen, 
  onClose, 
  onVerify, 
  title = "Enter PIN", 
  description = "Enter your PIN to continue",
  role = "admin" // "admin" or "manager"
}) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const pinRefs = useRef([]);

  useEffect(() => {
    if (isOpen) {
      setPin(['', '', '', '']);
      setError('');
      // Focus first input when modal opens
      setTimeout(() => pinRefs.current[0]?.focus(), 100);
    }
  }, [isOpen]);

  const handlePinChange = (index, value) => {
    if (value.length > 1) return;
    if (value && !/^\d+$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    if (value && index < 3) {
      pinRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (index === 3 && value) {
      const fullPin = [...newPin.slice(0, 3), value].join('');
      handleVerify(fullPin);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (pinToVerify = null) => {
    const pinString = pinToVerify || pin.join('');
    
    if (pinString.length !== 4) {
      setError('Please enter complete PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const isValid = await onVerify(pinString, role);
      if (!isValid) {
        setError('Incorrect PIN. Please try again.');
        setPin(['', '', '', '']);
        pinRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
      setPin(['', '', '', '']);
      pinRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className={`p-6 ${role === 'admin' ? 'bg-gradient-to-br from-purple-600 to-purple-700' : 'bg-gradient-to-br from-blue-600 to-blue-700'} text-white`}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Lock size={24} />
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} />
            </button>
          </div>
          <h2 className="text-xl font-bold">{title}</h2>
          <p className="text-sm mt-1 opacity-90">{description}</p>
        </div>

        {/* PIN Input */}
        <div className="p-8">
          <div className="space-y-6">
            <div className="space-y-3">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider text-center block">
                Enter {role === 'admin' ? 'Admin' : 'Manager'} PIN
              </label>
              <div className="flex gap-3 justify-center">
                {pin.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (pinRefs.current[index] = el)}
                    type="password"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={loading}
                    className={`w-14 h-16 text-center text-2xl font-bold border-2 rounded-xl outline-none transition-all ${
                      error 
                        ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100' 
                        : role === 'admin'
                        ? 'border-slate-300 focus:border-purple-500 focus:ring-4 focus:ring-purple-100'
                        : 'border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  />
                ))}
              </div>
              {error && (
                <p className="text-xs text-red-600 font-semibold text-center animate-in fade-in slide-in-from-top-1 duration-200">
                  {error}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleVerify()}
                disabled={loading || pin.join('').length !== 4}
                className={`flex-1 py-3 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  role === 'admin'
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PinVerificationModal;
